import type { DocHandle, DocumentId, Repo } from '@automerge/automerge-repo'
import { BranchLog, OBJECT_EXTENSION } from '../../log/index.js'
import { changeProject } from '../project/scalars.js'
import type { ProjectDoc } from '../project/types'
import { imm, str } from '../stored-string'
import {
  appendEvent,
  completionRule,
  hasMergeEvidence,
  IMPLEMENTED_EVENT,
  isMergeDriven,
  MERGE_EVENT,
  mayComplete,
  taskEvents,
} from './events'
import { cleanRef, mergeRefs, ownerSession, sameRef, taskRefs } from './refs'
import type {
  CreateTaskInput,
  Task,
  TaskEvent,
  TaskListOptions,
  TaskMutationResult,
  TaskRef,
  TaskRegistryEntry,
  TaskSummary,
  UpdateTaskInput,
} from './types'

/**
 * The branch a pre-S10 backfilled task named in prose rather than in a ref:
 * `[review-branch:feat/0319-tasks]` in its description. The one place that
 * marker is read, and only so the correction can replace it with a ref.
 */
function legacyReviewBranch(task: Task): string | null {
  const match = /\[review-branch:([^\]]+)\]/.exec(task.description ?? '')
  return match ? match[1].trim() : null
}

/** The list fields of a task, as the registry keeps them (RFC 0319 3e). Only
 *  defined fields, because Automerge refuses `undefined`. */
export function registryEntry(task: Task): TaskRegistryEntry {
  return {
    // One op each instead of one per character. A title is assigned whole, and
    // `updatedAt` is a 24-character ISO string — 24 ops every time a task is
    // touched, for a value nothing reads character by character.
    //
    // `status`, `priority` and `completion` are LEFT ALONE on purpose: they are
    // short enum-like words, and the places that compare them with `===` far
    // outweigh the few ops a wrapper would save. See `dataType` in the
    // document catalog for the same call.
    title: imm(task.title),
    status: task.status,
    ...(task.priority ? { priority: task.priority } : {}),
    ...(task.parentId ? { parentId: task.parentId } : {}),
    blockedBy: Array.isArray(task.blockedBy) ? [...task.blockedBy] : [],
    refs: taskRefs(task),
    // Derived when the task has none stored, so a list written before S10
    // still tells a slice from a manual task (RFC 0319 3g). The events are
    // deliberately not copied.
    completion: completionRule(task),
    updatedAt: imm(task.updatedAt),
  }
}

/**
 * Log-native task store (RFC 0232 over RFC 0219).
 *
 * A task is NOT a row in a separate project doc tree — it is an OBJECT ENTRY on
 * a session's log (`mod.object_created` at `path: task:<id>`, `objectType:'task'`,
 * pointing at a small content doc that holds the Task fields). The
 * session log is the same doc the thread rides on, so tasks sync to every surface
 * (web + CLI) exactly like the messages you already see — no parallel `tasksDocId`
 * repo, no share-manifest gap.
 *
 * Lenses are projections, not stored sets:
 *   - session lens  = project ONE session's log, filter objectType==='task'.
 *   - project lens = union the projections across the project's sessions.
 *
 * Completion/cancellation mutate the content doc (status) and append an
 * `object_edited` entry so the change is recorded causally on the log; a true
 * delete would append `object_deleted` (tombstone). Mirrors how files round-trip
 * (the object extension the session's log carries, RFC 0215).
 */
export class SessionTaskLog {
  /**
   * @param repo the project repo (the SAME synced repo the run uses).
   * @param announce RFC 0209 share hook: register a newly-minted content doc
   *   with the share policy so the daemon's WS adapter pushes it to the sync
   *   server. Without it the web sees the docId on the log but `repo.find()`
   *   returns document-unavailable (mirrors FileNamespace content-doc creation).
   */
  constructor(
    private readonly repo: Repo,
    private readonly announce?: (documentIds: string[]) => void,
    /** The project whose registry every write here keeps current (RFC 0319
     *  3e). A log opened without one only reads. */
    private readonly projectId?: string,
  ) {}

  private taskPath(id: string): string {
    return `task:${id}`
  }

  private async findWithTimeout<T>(
    id: string,
    budgetMs = 4000,
  ): Promise<DocHandle<T> | null> {
    const timeout = (ms: number) =>
      new Promise<null>((r) => setTimeout(() => r(null), ms))
    try {
      const handle = (await Promise.race([
        this.repo.find<T>(id as unknown as DocumentId),
        timeout(budgetMs),
      ])) as DocHandle<T> | null
      if (!handle) return null
      if (
        typeof (handle as { whenReady?: () => Promise<void> }).whenReady ===
        'function'
      ) {
        await Promise.race([
          (handle as { whenReady: () => Promise<void> }).whenReady(),
          timeout(budgetMs),
        ])
      }
      return handle
    } catch {
      return null
    }
  }

  /** A task as callers see it: plain JS with normalized refs and events. */
  private read(doc: Task): Task {
    const task = this.plainify(doc)
    return {
      ...task,
      refs: taskRefs(task),
      events: taskEvents(task),
      completion: completionRule(task),
    }
  }

  /** Plainify Automerge-encoded scalars (Text/value objects → plain JS). */
  private plainify<T>(doc: T): T {
    try {
      return JSON.parse(JSON.stringify(doc)) as T
    } catch {
      return doc
    }
  }

  /**
   * Create a task as an object entry on `sessionId`'s log. The content doc holds
   * the Task; `task.id` IS the content doc id (globally unique — no
   * `nextSequence` counter to collide on).
   */
  async create(
    sessionId: string | undefined,
    input: CreateTaskInput,
  ): Promise<Task> {
    const parent = input.parentId
      ? await this.findWithTimeout<Task>(input.parentId)
      : null
    if (input.parentId && !parent?.doc?.()?.id)
      throw new Error(`Parent task ${input.parentId} not found`)

    const handle = this.repo.create<Task>()
    const id = String(handle.documentId)
    const now = new Date().toISOString()

    // The making session first: its log holds the task's object entry.
    const refs = mergeRefs(
      sessionId
        ? [{ type: 'session', target: sessionId, role: 'created-by' }]
        : [],
      input.refs ?? [],
    )

    const task: Task = {
      id,
      title: input.title,
      description: input.description,
      status: 'pending',
      priority: input.priority,
      blockedBy: [],
      subtaskIds: [],
      refs,
      events: [],
      // Stored ONLY when the caller says so. Left absent, the rule is derived
      // from the refs on every read (RFC 0319 3g), so a task that gains a
      // branch ref later becomes a slice without anyone rewriting a field.
      completion: input.completion,
      parentId: input.parentId,
      parallelGroup: input.parallelGroup,
      createdAt: now,
      updatedAt: now,
    }
    handle.change((d: Task) => {
      // Automerge rejects `undefined`; only assign defined fields.
      for (const [k, v] of Object.entries(task)) {
        if (v !== undefined) (d as unknown as Record<string, unknown>)[k] = v
      }
    })

    // RFC 0209 — register the content doc for sync BEFORE the object entry, so
    // by the time the web projects the task path the content doc is reachable.
    try {
      this.announce?.([id])
    } catch {
      /* best effort */
    }

    if (sessionId) {
      await this.appendObjectEntry(sessionId as unknown as DocumentId, {
        path: this.taskPath(id),
        objectType: 'task',
        docId: id,
      })
    }
    const created = this.read(handle.doc() as Task)
    await this.writeRegistry(created)

    // The other end of the subtask link (RFC 0319 3b): written in the same
    // call, so a parent never lacks a child that names it.
    if (parent) {
      parent.change((d: Task) => {
        if (!Array.isArray(d.subtaskIds)) d.subtaskIds = []
        if (!d.subtaskIds.includes(id)) d.subtaskIds.push(id)
        d.updatedAt = now
      })
      await this.recordEdit(this.read(parent.doc() as Task))
    }
    return created
  }

  /** Session lens: project this session's log, read each task content doc. */
  async listForSession(
    sessionId: string,
    options?: { includeCompleted?: boolean },
  ): Promise<Task[]> {
    const sessionHandle = await this.findWithTimeout(sessionId)
    if (!sessionHandle) return []
    const view = new BranchLog(sessionHandle as never).projectObjects()
    const out: Task[] = []
    for (const ref of view.values()) {
      if (ref.objectType !== 'task') continue
      const tHandle = await this.findWithTimeout<Task>(String(ref.docId))
      const doc = tHandle?.doc?.()
      if (doc?.id) out.push(this.read(doc))
    }
    return this.applyVisibility(out, options?.includeCompleted)
  }

  /**
   * The project's task list from its registry (RFC 0319 3e): ONE read of the
   * project doc, no session and no task opened. A project whose registry has
   * never been written is backfilled from the session logs first.
   */
  async summaries(
    projectId: string,
    options?: Pick<TaskListOptions, 'includeCompleted' | 'status' | 'priority'>,
  ): Promise<TaskSummary[]> {
    const handle = await this.findWithTimeout<ProjectDoc>(projectId)
    const doc = handle?.doc?.()
    if (!doc) return []
    const map = doc.tasks
      ? this.plainify(doc.tasks)
      : await this.rebuildRegistry(projectId)
    let tasks: TaskSummary[] = Object.entries(map).map(([id, entry]) => ({
      id,
      ...entry,
      // The stored title and timestamp are one op each, so they arrive as
      // wrappers; a summary promises plain strings.
      title: str(entry.title),
      updatedAt: str(entry.updatedAt),
      blockedBy: Array.isArray(entry.blockedBy) ? entry.blockedBy : [],
      // Registries written before RFC 0319 S10 have no refs. Keep the
      // project/session lenses readable while those entries are backfilled.
      refs: Array.isArray(entry.refs) ? entry.refs : [],
    }))
    if (options?.status)
      tasks = tasks.filter((t) => t.status === options.status)
    if (options?.priority)
      tasks = tasks.filter((t) => t.priority === options.priority)
    return this.applyVisibility(tasks, options?.includeCompleted)
  }

  /**
   * The session lens (RFC 0319 3d): the tasks whose refs name `sessionId`,
   * the ones it made and the ones it worked. From the registry, so no session
   * log is walked.
   */
  async forSession(
    projectId: string,
    sessionId: string,
    options?: { includeCompleted?: boolean },
  ): Promise<TaskSummary[]> {
    const all = await this.summaries(projectId, {
      includeCompleted: options?.includeCompleted === true,
    })
    return all.filter((t) => t.refs.some((r) => r.target === sessionId))
  }

  /**
   * Project lens, whole tasks: the registry says which, then each task doc is
   * read. One read per task, never one per session.
   */
  async listForProject(
    projectId: string,
    options?: TaskListOptions,
  ): Promise<Task[]> {
    const listed = await this.summaries(projectId, {
      includeCompleted: true,
      ...(options?.status ? { status: options.status } : {}),
      ...(options?.priority ? { priority: options.priority } : {}),
    })
    const found = await Promise.all(listed.map((t) => this.get(t.id)))
    let tasks = found.filter((t): t is Task => t !== null)
    if (options?.assignee)
      tasks = tasks.filter((t) =>
        t.refs.some(
          (r) =>
            r.type === 'person' &&
            r.target === options.assignee &&
            r.role === 'owner',
        ),
      )
    if (options?.sourceType)
      tasks = tasks.filter((t) =>
        t.refs.some((r) => r.type === options.sourceType),
      )
    return this.applyVisibility(tasks, options?.includeCompleted)
  }

  /**
   * Rebuild the registry from the task docs the project's session logs name.
   * Run by the first read of a project that has none; also the recovery when a
   * registry is lost or was written by an older build that did not keep it.
   */
  async rebuildRegistry(
    projectId: string,
  ): Promise<Record<string, TaskRegistryEntry>> {
    const handle = await this.findWithTimeout<ProjectDoc>(projectId)
    const doc = handle?.doc?.()
    if (!handle || !doc) return {}
    const sessionIds = Array.isArray(doc.sessionIds)
      ? doc.sessionIds.map(String)
      : []
    const map: Record<string, TaskRegistryEntry> = {}
    for (const sid of sessionIds) {
      for (const t of await this.listForSession(sid, {
        includeCompleted: true,
      })) {
        map[t.id] = registryEntry(t)
      }
    }
    changeProject(handle, (d) => {
      if (!d.tasks) d.tasks = {}
      for (const [id, entry] of Object.entries(map)) d.tasks[id] = entry
    })
    return map
  }

  /**
   * Put right the tasks that were completed before their branch merged (RFC
   * 0319 S10).
   *
   * The backfill that turned an RFC's build order into tasks read a ticked
   * line as a completed task, so every slice of an unmerged branch reads
   * complete. This walks the project's completed tasks, and for each slice
   * with no merge evidence returns it to `active`, keeping what was there as
   * an `implemented` event: the old `completedAt` as the event's time and the
   * old completion note as its note. Nothing is lost, and the RFC line
   * unticks because it is drawn from the status.
   *
   * A task written before S10 has no branch ref, only the
   * `[review-branch:<name>]` marker the backfill wrote into its description.
   * That is read here, once, and turned into a real ref, so the rule has
   * something to stand on afterwards.
   *
   * Idempotent: a second run finds nothing complete without evidence.
   * `dryRun` returns what it would touch and writes nothing.
   */
  async correctUnmergedCompletions(
    projectId: string,
    options?: { dryRun?: boolean },
  ): Promise<Task[]> {
    const summaries = await this.summaries(projectId, {
      includeCompleted: true,
    })
    const corrected: Task[] = []
    for (const summary of summaries) {
      if (summary.status !== 'complete') continue
      const handle = await this.findWithTimeout<Task>(summary.id)
      // The RAW doc, not `this.read`: reading fills `completion` in with the
      // derived value, and the question here is what the task actually stores,
      // so an explicit `manual` is honoured and an absent one is derived from
      // the refs the correction is about to add.
      const task = handle?.doc?.() ? this.plainify(handle.doc() as Task) : null
      if (!handle || !task || task.status !== 'complete') continue
      const branch = legacyReviewBranch(task)
      const refs = branch
        ? [{ type: 'branch', target: branch, role: 'about' }]
        : []
      if (
        !isMergeDriven({ ...task, refs: [...taskRefs(task), ...refs] }) ||
        hasMergeEvidence(task)
      )
        continue
      if (options?.dryRun) {
        corrected.push(this.read(task))
        continue
      }

      const now = new Date().toISOString()
      handle.change((d: Task) => {
        if (refs.length > 0) this.addRefs(d, refs)
        appendEvent(
          d,
          {
            type: IMPLEMENTED_EVENT,
            at: d.completedAt || now,
            ...(d.completionNote ? { note: d.completionNote } : {}),
          },
          { once: true },
        )
        d.status = 'active'
        delete d.completedAt
        delete d.completionNote
        d.updatedAt = now
      })
      const updated = this.read(handle.doc() as Task)
      await this.recordEdit(updated, projectId)
      corrected.push(updated)
    }
    return corrected
  }

  /** Keep the registry's copy of `task` current: the same write as the task
   *  doc, from the caller's point of view (RFC 0319 3e). */
  private async writeRegistry(
    task: Task,
    projectId = this.projectId,
  ): Promise<void> {
    if (!projectId) return
    const handle = await this.findWithTimeout<ProjectDoc>(projectId)
    if (!handle) return
    const entry = registryEntry(task)
    changeProject(handle, (d) => {
      if (!d.tasks) d.tasks = {}
      d.tasks[task.id] = entry
    })
  }

  /**
   * A session takes a pending task (RFC 0304 3c): it becomes active with that
   * session as its worker, so the session's first turn already has it as its
   * goal. Returns null when the task is gone or someone else took it first.
   */
  async take(id: string, sessionId: string): Promise<Task | null> {
    const task = await this.get(id)
    if (!task || task.status !== 'pending') return null
    return this.update(task.id, {
      status: 'active',
      addRefs: [{ type: 'session', target: sessionId, role: 'worked-by' }],
    })
  }

  async get(id: string): Promise<Task | null> {
    const handle = await this.findWithTimeout<Task>(id)
    const doc = handle?.doc?.()
    return doc?.id ? this.read(doc) : null
  }

  /**
   * Edit a task's fields.
   *
   * A status of `complete` goes through the same rule `complete()` uses (RFC
   * 0319 3g): asking a merge-driven slice to complete without merge evidence
   * leaves it `active` with an `implemented` event, so a tick on a checkbox
   * cannot claim a merge that has not happened. The returned task carries the
   * status that was actually written.
   */
  async update(id: string, updates: UpdateTaskInput): Promise<Task> {
    const handle = await this.findWithTimeout<Task>(id)
    if (!handle) throw new Error(`Task ${id} not found`)
    const now = new Date().toISOString()
    const more = updates.addRefs ?? []
    const before = this.read(handle.doc() as Task)
    const moving = updates.parentId !== undefined
    const nextParentId = updates.parentId ?? undefined
    if (nextParentId === id) throw new Error('A task cannot be its own parent')

    let nextParent: DocHandle<Task> | null = null
    if (moving && nextParentId && nextParentId !== before.parentId) {
      nextParent = await this.findWithTimeout<Task>(nextParentId)
      if (!nextParent?.doc?.()?.id)
        throw new Error(`Parent task ${nextParentId} not found`)

      // Walk upward before writing either end. A cycle would make every list
      // renderer choose between recursion and silently dropping part of the
      // task graph, so reject it at the mutation boundary.
      const seen = new Set<string>([id])
      let ancestor = this.read(nextParent.doc() as Task)
      while (ancestor.parentId) {
        if (seen.has(ancestor.id))
          throw new Error('A task cannot be moved under one of its subtasks')
        seen.add(ancestor.id)
        const parent = await this.findWithTimeout<Task>(ancestor.parentId)
        if (!parent?.doc?.()?.id) break
        ancestor = this.read(parent.doc() as Task)
      }
      if (seen.has(ancestor.id))
        throw new Error('A task cannot be moved under one of its subtasks')
    }
    const asking = updates.status === 'complete'
    const gate = asking
      ? mayComplete(
          { ...before, completion: updates.completion ?? before.completion },
          more,
        )
      : ({ allowed: true } as const)
    handle.change((d: Task) => {
      if (updates.title != null) d.title = updates.title
      if (updates.description != null) d.description = updates.description
      if (updates.priority != null) d.priority = updates.priority
      if (updates.completion != null) d.completion = updates.completion
      if (moving) {
        if (nextParentId) d.parentId = nextParentId
        else delete d.parentId
      }
      if (updates.status != null) {
        d.status = asking && !gate.allowed ? 'active' : updates.status
        if (asking && gate.allowed) d.completedAt = now
        else if (updates.status !== 'complete') delete d.completedAt
      }
      if (asking && !gate.allowed) {
        appendEvent(
          d,
          { type: IMPLEMENTED_EVENT, at: now, note: updates.completionNote },
          { once: true },
        )
      } else if (updates.completionNote != null) {
        d.completionNote = updates.completionNote
      }
      if (more.length > 0) this.addRefs(d, more)
      if (updates.removeRefs?.length) this.removeRefs(d, updates.removeRefs)
      d.updatedAt = now
    })
    const task = this.read(handle.doc() as Task)
    await this.recordEdit(task)

    if (moving && before.parentId !== nextParentId) {
      if (before.parentId) {
        const oldParent = await this.findWithTimeout<Task>(before.parentId)
        if (oldParent?.doc?.()?.id) {
          oldParent.change((d: Task) => {
            if (!Array.isArray(d.subtaskIds)) d.subtaskIds = []
            d.subtaskIds = d.subtaskIds.filter((childId) => childId !== id)
            d.updatedAt = now
          })
          await this.recordEdit(this.read(oldParent.doc() as Task))
        }
      }
      if (nextParentId) {
        nextParent ??= await this.findWithTimeout<Task>(nextParentId)
        if (!nextParent?.doc?.()?.id)
          throw new Error(`Parent task ${nextParentId} not found`)
        nextParent.change((d: Task) => {
          if (!Array.isArray(d.subtaskIds)) d.subtaskIds = []
          if (!d.subtaskIds.includes(id)) d.subtaskIds.push(id)
          d.updatedAt = now
        })
        await this.recordEdit(this.read(nextParent.doc() as Task))
      }
    }
    return task
  }

  /**
   * Append one event to a task's history (RFC 0319 3g): what happened, not
   * what it is. Status does not move, which is the point: a review verdict, a
   * commit or an app's own event is evidence a surface can read without any
   * of them pretending to be a state.
   */
  async recordEvent(id: string, event: TaskEvent): Promise<Task> {
    const handle = await this.findWithTimeout<Task>(id)
    if (!handle) throw new Error(`Task ${id} not found`)
    const now = new Date().toISOString()
    handle.change((d: Task) => {
      appendEvent(d, { ...event, at: event.at || now })
      if (Array.isArray(event.refs) && event.refs.length > 0)
        this.addRefs(d, event.refs)
      d.updatedAt = now
    })
    const task = this.read(handle.doc() as Task)
    await this.recordEdit(task)
    return task
  }

  /**
   * A checkbox line that arrives already ticked (RFC 0319 3d, 3g).
   *
   * The bug this closes: a build order whose lines were ticked because the
   * code was written, imported as a set of completed tasks on a branch nobody
   * has merged. A tick is evidence of implementation, not of a merge, so on a
   * slice it becomes an `implemented` event and the task stays open. On a
   * manual task it still completes, because there a tick is all the evidence
   * there is. Running it twice adds nothing.
   */
  async importChecked(
    projectId: string,
    id: string,
    note?: string,
  ): Promise<TaskMutationResult> {
    const task = await this.get(id)
    if (!task) throw new Error(`Task ${id} not found`)
    const gate = mayComplete(task)
    if (gate.allowed) {
      return this.complete(projectId, id, note ?? 'Imported from a ticked line')
    }
    const handle = await this.findWithTimeout<Task>(id)
    if (!handle) throw new Error(`Task ${id} not found`)
    const now = new Date().toISOString()
    handle.change((d: Task) => {
      appendEvent(
        d,
        {
          type: IMPLEMENTED_EVENT,
          at: now,
          note: note ?? 'Imported from a ticked line',
        },
        { once: true },
      )
      if (d.status === 'pending') d.status = 'active'
      d.updatedAt = now
    })
    const updated = this.read(handle.doc() as Task)
    await this.recordEdit(updated, projectId)
    return {
      task: updated,
      unblockedTasks: [],
      deferred: { reason: gate.reason },
    }
  }

  /**
   * Complete a task, if its rule allows it (RFC 0319 3g).
   *
   * A merge-driven slice asked to complete without merge evidence does not
   * complete: it records an `implemented` event, stays active, and comes back
   * with `deferred` set so the caller reports what actually happened rather
   * than a completion that did not. Merge evidence is a `closed-by` ref or a
   * `merged` event already on the task.
   */
  async complete(
    projectId: string,
    id: string,
    note?: string,
    /** What closed it, as refs: a PR with role `closed-by` (RFC 0319 3f). */
    closedBy: TaskRef[] = [],
  ): Promise<TaskMutationResult> {
    const handle = await this.findWithTimeout<Task>(id)
    if (!handle) throw new Error(`Task ${id} not found`)
    const now = new Date().toISOString()
    const before = this.read(handle.doc() as Task)
    const gate = mayComplete(before, closedBy)
    if (!gate.allowed) {
      handle.change((d: Task) => {
        appendEvent(
          d,
          { type: IMPLEMENTED_EVENT, at: now, note },
          { once: true },
        )
        if (d.status === 'pending') d.status = 'active'
        if (closedBy.length > 0) this.addRefs(d, closedBy)
        d.updatedAt = now
      })
      const deferredTask = this.read(handle.doc() as Task)
      await this.recordEdit(deferredTask, projectId)
      return {
        task: deferredTask,
        unblockedTasks: [],
        deferred: { reason: gate.reason },
      }
    }
    // The merge is the thing that completed it, so it is written as evidence
    // beside the status rather than left implicit in `completedAt`.
    const merged =
      isMergeDriven(before) && !hasMergeEvidence(before)
        ? { type: MERGE_EVENT, at: now, refs: closedBy, note }
        : null
    handle.change((d: Task) => {
      d.status = 'complete'
      d.completedAt = now
      d.updatedAt = now
      if (note != null) d.completionNote = note
      if (closedBy.length > 0) this.addRefs(d, closedBy)
      if (merged) appendEvent(d, merged, { once: true })
    })
    const task = this.read(handle.doc() as Task)
    await this.recordEdit(task, projectId)

    // Unblock any task — in ANY session — that was waiting on this one and now
    // has all its dependencies complete. Dependents are resolved via the
    // project lens (not just this task's session), so a cross-session
    // dependency flips to ready the moment its blocker completes.
    const unblockedTasks: Task[] = []
    const all = await this.summaries(projectId, { includeCompleted: true })
    const isComplete = new Map(all.map((t) => [t.id, t.status === 'complete']))
    isComplete.set(task.id, true)
    for (const candidate of all) {
      if (candidate.id === task.id) continue
      if (candidate.status !== 'blocked') continue
      if (
        !Array.isArray(candidate.blockedBy) ||
        !candidate.blockedBy.includes(task.id)
      )
        continue
      const allDepsComplete = candidate.blockedBy.every(
        (depId) => isComplete.get(depId) === true,
      )
      if (!allDepsComplete) continue
      const depHandle = await this.findWithTimeout<Task>(candidate.id)
      if (!depHandle) continue
      depHandle.change((d: Task) => {
        if (d.status === 'blocked') d.status = 'pending'
        d.updatedAt = now
      })
      const updated = this.read(depHandle.doc() as Task)
      await this.recordEdit(updated, projectId)
      unblockedTasks.push(updated)
    }
    return { task, unblockedTasks }
  }

  async cancel(id: string): Promise<Task> {
    const handle = await this.findWithTimeout<Task>(id)
    if (!handle) throw new Error(`Task ${id} not found`)
    const now = new Date().toISOString()
    handle.change((d: Task) => {
      d.status = 'cancelled'
      d.updatedAt = now
    })
    const task = this.read(handle.doc() as Task)
    await this.recordEdit(task)
    return task
  }

  /** Add refs inside a change. */
  private addRefs(d: Task, more: TaskRef[]): void {
    if (!Array.isArray(d.refs)) d.refs = []
    for (const raw of more) {
      const ref = cleanRef(raw)
      if (ref && !d.refs.some((r) => sameRef(r, ref))) d.refs.push(ref)
    }
  }

  /** Remove refs inside a change, back to front so indexes stay valid. */
  private removeRefs(d: Task, gone: TaskRef[]): void {
    if (!Array.isArray(d.refs)) return
    const drop = gone.map(cleanRef).filter((r): r is TaskRef => r !== null)
    for (let i = d.refs.length - 1; i >= 0; i--) {
      const ref = d.refs[i]
      if (ref && drop.some((r) => sameRef(r, ref))) d.refs.splice(i, 1)
    }
  }

  /** Append an `object_edited` entry to the task's owning session log, and
   *  keep the registry's copy current. */
  private async recordEdit(
    task: Task,
    projectId = this.projectId,
  ): Promise<void> {
    await this.writeRegistry(task, projectId)
    const sessionId = ownerSession(task)
    if (!sessionId) return
    await this.appendObjectEntry(
      sessionId as unknown as DocumentId,
      { path: this.taskPath(task.id), objectType: 'task', docId: task.id },
      { edited: true },
    )
  }

  /**
   * The session's own record that a task was created or edited: an object
   * extension on its log (RFC 0215). Was `SessionRepository.appendObjectEntry`;
   * inlined in RFC 0314 S6 when that repository went to reference.
   */
  private async appendObjectEntry(
    sessionId: DocumentId,
    payload: { path: string; objectType: string; docId?: string | null },
    options?: { edited?: boolean; deleted?: boolean },
  ): Promise<string> {
    const handle = await this.repo.find(sessionId)
    if (!handle) throw new Error(`Session not found: ${sessionId}`)
    const h = handle as unknown as {
      request?: () => void
      whenReady?: () => Promise<unknown>
    }
    h.request?.()
    if (h.whenReady) {
      await Promise.race([
        h.whenReady(),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ])
    }
    const log = new BranchLog(handle as never)
    const extensionType = options?.deleted
      ? OBJECT_EXTENSION.deleted
      : options?.edited
        ? OBJECT_EXTENSION.edited
        : OBJECT_EXTENSION.created
    return log.appendExtension(
      extensionType,
      1,
      {
        path: payload.path,
        objectType: payload.objectType,
        docId: payload.docId == null ? null : String(payload.docId),
      },
      log.heads(),
    )
  }

  /**
   * Add `dependsOnIds` to a task's `blockedBy`, log-native. Resolves ids across
   * the whole project (deps may live on other sessions), rejects cycles, and
   * flips the task to `blocked` if any dependency is still open. The task's id
   * IS its content doc id, so the edit lands on that doc + records an edit on
   * the owning session.
   */
  async addDependency(
    projectId: string,
    idOrPrefix: string,
    dependsOnIds: string[],
  ): Promise<Task> {
    const all = await this.summaries(projectId, { includeCompleted: true })
    const resolve = (key: string): TaskSummary | undefined =>
      all.find((t) => t.id === key) ?? all.find((t) => t.id.startsWith(key))

    const task = resolve(idOrPrefix)
    if (!task) throw new Error(`Task not found: ${idOrPrefix}`)

    const resolvedDepIds: string[] = []
    for (const depKey of dependsOnIds) {
      const dep = resolve(depKey)
      if (!dep) throw new Error(`Dependency task not found: ${depKey}`)
      if (this.hasCycle(all, dep.id, task.id)) {
        throw new Error(
          `Adding dependency would create a cycle: ${task.id} -> ${dep.id}`,
        )
      }
      resolvedDepIds.push(dep.id)
    }

    const handle = await this.findWithTimeout<Task>(task.id)
    if (!handle) throw new Error(`Task ${task.id} not found`)
    const now = new Date().toISOString()
    handle.change((d: Task) => {
      const current = Array.isArray(d.blockedBy) ? d.blockedBy : []
      d.blockedBy = [...new Set([...current, ...resolvedDepIds])]
      const allDepsComplete = d.blockedBy.every(
        (depId) => all.find((c) => c.id === depId)?.status === 'complete',
      )
      if (!allDepsComplete && d.status === 'pending') d.status = 'blocked'
      d.updatedAt = now
    })
    const result = this.read(handle.doc() as Task)
    await this.recordEdit(result, projectId)
    return result
  }

  /** Cycle check over the project task set (DFS on blockedBy). */
  private hasCycle(
    tasks: Array<Pick<Task, 'id' | 'blockedBy'>>,
    fromId: string,
    targetId: string,
  ): boolean {
    const visited = new Set<string>()
    const stack = [fromId]
    while (stack.length > 0) {
      const current = stack.pop()!
      if (current === targetId) return true
      if (visited.has(current)) continue
      visited.add(current)
      const task = tasks.find((entry) => entry.id === current)
      if (!task) continue
      for (const depId of task.blockedBy) stack.push(depId)
    }
    return false
  }

  private applyVisibility<T extends Pick<Task, 'status'>>(
    tasks: T[],
    includeCompleted?: boolean,
  ): T[] {
    if (includeCompleted) return tasks
    return tasks.filter(
      (t) => t.status !== 'complete' && t.status !== 'cancelled',
    )
  }
}
